import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchWork } from "@/studio/lib/projects";
import UIMock from "@/studio/components/UIMock";

const ease = [0.16, 1, 0.3, 1];

function ProjectCard({ p, i }) {
  const flipped = i % 2 === 1;
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el || window.matchMedia("(pointer: coarse)").matches) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(1300px) rotateX(${(-py * 3.5).toFixed(2)}deg) rotateY(${(px * 3.5).toFixed(2)}deg) translate3d(${(px * 10).toFixed(1)}px, ${(py * 10).toFixed(1)}px, 0)`;
  };
  const onLeave = () => {
    const el = ref.current;
    if (el) el.style.transform = "perspective(1300px) rotateX(0deg) rotateY(0deg) translate3d(0,0,0)";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease }}
    >
      <Link
        ref={ref}
        to={`/work/${p.slug}`}
        data-cursor="big"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ transition: "transform 0.5s cubic-bezier(0.16,1,0.3,1)" }}
        className="group block overflow-hidden rounded-[32px] border border-[var(--line)] bg-[var(--paper-2)] shadow-soft transition-shadow duration-500 hover:shadow-premium"
      >
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 ${
            flipped ? "lg:[&>*:first-child]:order-2" : ""
          }`}
        >
          {/* text */}
          <div className="flex flex-col justify-between p-8 md:p-12">
            <div>
              <div className="flex items-center gap-3">
                <span className="chip-lime">{p.category}</span>
                <span className="label text-[var(--ink-faint)]">{p.year}</span>
              </div>
              <h3 className="ff-display mt-7 text-5xl font-extrabold tracking-tight md:text-7xl">
                {p.name}
              </h3>
              <p className="ff-serif mt-3 text-xl text-[var(--ink-soft)] md:text-2xl">
                {p.tagline}
              </p>
              <p className="mt-6 max-w-md text-[14px] leading-relaxed text-[var(--ink-soft)]">
                {p.challenge}
              </p>
            </div>

            <div className="mt-10">
              <div className="flex flex-wrap gap-2">
                {p.stack.slice(0, 5).map((s) => (
                  <span
                    key={s}
                    className="rounded-full border border-[var(--line)] px-3 py-1 text-[12px] font-medium text-[var(--ink-soft)]"
                  >
                    {s}
                  </span>
                ))}
              </div>
              <div className="mt-7 inline-flex items-center gap-3 text-[14px] font-semibold">
                View case study
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--ink)] text-[var(--ink-on)] transition-transform duration-500 group-hover:translate-x-1">
                  →
                </span>
              </div>
            </div>
          </div>

          {/* visual */}
          <div className="relative min-h-[320px] overflow-hidden bg-[#080808] p-7 md:min-h-[460px] md:p-10">
            <div
              className="pointer-events-none absolute inset-0 opacity-60"
              style={{
                background:
                  "radial-gradient(120% 90% at 80% 10%, rgba(228,255,0,0.12), transparent 60%)",
              }}
            />
            <div className="relative flex h-full items-center transition-transform duration-700 ease-out group-hover:scale-[1.03]">
              {p.cover ? (
                <img
                  src={p.cover}
                  alt={p.name}
                  loading="lazy"
                  className="h-full max-h-[400px] w-full rounded-2xl object-cover"
                />
              ) : (
                <UIMock kind={p.mock} />
              )}
            </div>
            <span className="ff-display absolute right-6 top-6 text-sm font-bold text-white/30">
              {p.index} / {p.total || "04"}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function SelectedWork() {
  const [work, setWork] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchWork().then((items) => {
      if (alive) setWork(items);
    });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section id="work" className="relative bg-[var(--paper)] py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <span className="label text-[var(--ink-soft)]">(03) — Selected work</span>
            <h2 className="ff-display mt-6 text-5xl font-extrabold leading-[0.92] tracking-tight md:text-8xl">
              SELECTED
              <br />
              WORK
            </h2>
          </div>
          <p className="max-w-xs text-[15px] leading-relaxed text-[var(--ink-soft)]">
            A small number of projects, built with disproportionate care. Each
            one engineered end-to-end.
          </p>
        </div>

        <div className="mt-16 space-y-8 md:mt-20">
          {work.map((p, i) => (
            <ProjectCard key={p.slug} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
